export type PreviewImageSize = 'xs' | 'sm' | 'md'
export type PreviewImageSources = Partial<Record<PreviewImageSize, string>>

// These are the existing generated versions, not guaranteed intrinsic widths:
// the image service keeps smaller originals at their original resolution.
const versionWidths: Record<PreviewImageSize, number> = { xs: 100, sm: 350, md: 560 }
const versionSizes: PreviewImageSize[] = ['xs', 'sm', 'md']

export const previewImageSize = (sources: PreviewImageSources, requiredWidth: number): PreviewImageSize | undefined => {
  const available = versionSizes.filter(size => sources[size])
  return available.find(size => versionWidths[size] >= requiredWidth) || available[available.length - 1]
}

export const containedImageWidth = (width: number, height: number, naturalWidth: number, naturalHeight: number) => (
  width > 0 && height > 0 && naturalWidth > 0 && naturalHeight > 0
    ? Math.min(width, height * naturalWidth / naturalHeight)
    : 0
)

/** Upgrade a lazy thumbnail only after its real proportions and display box are known. */
export const observePreviewImage = (
  image: HTMLImageElement,
  sources: PreviewImageSources,
  fallback: string,
) => {
  let currentSize = versionSizes.find(size => sources[size] === image.getAttribute('src'))
  let originalSizeReached = false
  let failed = false
  let observer: ResizeObserver | undefined

  const fallbackImage = () => {
    failed = true
    observer?.disconnect()
    if (image.getAttribute('src') !== fallback) image.src = fallback
  }

  const update = () => {
    if (failed || !currentSize || !image.complete || !image.naturalWidth || originalSizeReached) return
    const styles = window.getComputedStyle(image)
    const number = (value: string) => Number.parseFloat(value) || 0
    // clientWidth/clientHeight include padding, but object-fit uses the content box.
    const width = image.clientWidth - number(styles.paddingLeft) - number(styles.paddingRight)
    const height = image.clientHeight - number(styles.paddingTop) - number(styles.paddingBottom)
    const displayedWidth = containedImageWidth(width, height, image.naturalWidth, image.naturalHeight)
    if (!displayedWidth) return // A hidden carousel can be measured when it becomes visible.
    const requiredWidth = Math.ceil(displayedWidth * (window.devicePixelRatio || 1))
    if (image.naturalWidth >= requiredWidth) return
    const nextSize = previewImageSize(sources, requiredWidth)
    const nextSource = nextSize ? sources[nextSize] : undefined
    // Keep a loaded larger image on shrink; this avoids repeat loads and resize loops.
    if (nextSize && nextSource && versionWidths[nextSize] > versionWidths[currentSize]) {
      currentSize = nextSize
      image.src = nextSource
    }
  }

  const onLoad = () => {
    if (!image.naturalWidth || !image.naturalHeight) return
    image.width = image.naturalWidth
    image.height = image.naturalHeight
    const loadedSize = failed ? versionSizes.find(size => sources[size] === fallback) : currentSize
    if (loadedSize && image.naturalWidth < versionWidths[loadedSize]) {
      originalSizeReached = true
      // A smaller original must not be enlarged just to fill the card.
      image.style.setProperty('object-fit', 'scale-down', 'important')
    }
    update()
  }

  image.addEventListener('load', onLoad)
  image.addEventListener('error', fallbackImage)
  if (!sources.xs || typeof ResizeObserver === 'undefined') {
    fallbackImage()
  } else {
    observer = new ResizeObserver(update)
    observer.observe(image)
    window.addEventListener('resize', update)
  }
  if (image.complete) {
    if (image.naturalWidth) onLoad()
    else fallbackImage() // The lazy request may have failed before hydration attached listeners.
  }

  return () => {
    observer?.disconnect()
    window.removeEventListener('resize', update)
    image.removeEventListener('load', onLoad)
    image.removeEventListener('error', fallbackImage)
  }
}
